/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebFont from 'webfontloader';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';

import { defaultUserCustomTexts, shouldRender, toHex } from '../../../shared/form-lib';
import { selectFormCalculations, selectFormRules } from '../../form/selectors';
import { FormUtils, validatePresentPage } from '../../form/utils';
import { formDesignProps } from '../../form_fields/ctrl_proptype';
import { FormContext } from '../../shared/contexts/form-context';
import { collectAnalytics } from '../../form/slice-actions/form';
import { isTriggerField } from '../../../shared/logic_utils';

import ManuallyReplayOfflineSubmission from './manually-replay-offline-submission';
import PreviewFormField from '../../form_fields/components/preview-renderfield';
import Timer from '../../form_fields/components/timer/timer';
import ErrorBoundary from '../../errors/components';
import ThirdPartyShare from '../third-party-share';
import PaymentModal from './form-payment-modal';
import ESignPreviewModal from './e-sign/modal';
import FormWatermark from './form-watermark';
import Pagination from './form-pagination';
import FormHeader from './form-header';
import FormFooter from './form-footer';
import {
  FormPageBackgroundWrapper,
  FormControlsFlexWrapper,
  FormControlsWrapper,
  FormPageBackground,
  ReshowIntroPage,
  FormControls,
  Container,
} from './form-preview.styled';
import {
  doPageApplyCalculations,
  doReshowIntroPage,
  doPageApplyLogic,
  updateFieldError,
} from '../../form/reducer/form';
import {
  formPageBackgroundStyles,
  isCalcOperandField,
  isHiddenField,
  formStyles,
  replaceAt,
} from '../../../shared/utils';

const getFormLayout = (formDesign) => {
  if (formDesign.design.form_layout === 'side_layout right_layout') return 'flex-end';
  if (formDesign.design.form_layout === 'side_layout left_layout') return 'flex-start';
  return 'center';
};

const NewFormPreview = ({ form, rowCtrls, formDesign }) => {
  const {
    phoneNumberFields,
    triggeringFields,
    openPaymentModal,
    editingResponse,
    hasIntroMessage,
    reshowIntroPage,
    userCustomTexts,
    hiddenFields,
    payments,
    form_id,
    eSign,
  } = useSelector((state) => state.form);

  const { currentPageNo, inForm, inCustomise, setCurrentPageNo } = useContext(FormContext);
  const formCalculations = useSelector((state) => inForm && selectFormCalculations(state));
  const formRules = useSelector((state) => inForm && selectFormRules(state));
  const newCss = replaceAt(formDesign.old.form_external_css, 0, '');
  const formUtils = useMemo(() => new FormUtils(), []);
  const { onBlurForm, onFocusForm } = formUtils;
  const formBodyRef = useRef(null);
  const dispatch = useDispatch();

  const [isSubmissionTriggered, triggerFormSubmission] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(false);
  const [percent, setPercent] = useState(0);
  const [timeUp, setTimeUp] = useState({
    quizEnd: false,
    eventEnd: false,
  });

  const {
    definition: { pages },
    form_options,
    options,
  } = form;

  const {
    enable_esignature: enableESignature,
    social_options: socialOptions,
    timer_options: timerOptions,
    enable_timer: enableTimer,
  } = inForm ? form_options : options;

  const visiblePages = pages.filter((p) => !hiddenFields[p.internal_id]);
  const formPages = inForm ? visiblePages : pages;
  const presentPage = formPages[currentPageNo - 1];
  const noOfPages = formPages.length;
  const {
    timer_countdown_to,
    timer_description,
    timer_finish_do,
    timer_position,
    timer_purpose,
    timer_display,
    timer,
  } = timerOptions;

  const isPreviewingFormOnOtherPages = window.location.search?.includes('FPLS_PREVIEWING');
  const isEditedByUser = window.location.search?.includes('user');

  const formBodyStyles = {
    // padding: `${formDesign.design.form_body_padding / 2}px ${formDesign.design.form_body_padding * 1.1}px`,
    padding: `0px ${formDesign.design.form_body_padding}px`,
    marginBottom: 25,
    marginTop: 25,
  };

  const scrollToTop = () => {
    const props = {
      behavior: 'smooth',
      left: 0,
      top: 0,
    };
    if (inForm) return window.scroll(props);
    return formBodyRef.current.scrollTo(props);
  };

  useEffect(() => {
    if (formDesign.old.form_font) {
      WebFont.load({
        google: {
          families: [`${formDesign.old.form_font}:300,400,500,600,700`],
        },
      });
    }
  }, [formDesign.old.form_font]);

  useEffect(() => {
    scrollToTop();
    if (inForm) dispatch(collectAnalytics());
  }, []);

  useEffect(() => {
    if (inForm) {
      window.addEventListener('focus', onFocusForm);
      window.addEventListener('blur', onBlurForm);
    }
    return () => {
      window.removeEventListener('focus', onFocusForm);
      window.removeEventListener('blur', onBlurForm);
    };
  }, [inForm]);

  useEffect(() => {
    setPercent(Math.round((currentPageNo / noOfPages) * 100));
  }, [currentPageNo, noOfPages]);

  useEffect(() => {
    if (!presentPage) {
      setCurrentPageNo((prev) => (prev !== 0 ? prev - 1 : prev));
    }
  }, [presentPage, setCurrentPageNo]);

  const changePage = async (delta) => {
    function enableChangePage() {
      if (!progress) setProgress(true);
      if (delta > 0 && currentPageNo === noOfPages) return;
      setCurrentPageNo(currentPageNo + delta);
    }

    if (inForm && !isPreviewingFormOnOtherPages) {
      const responseStore = form.response_store;
      const pageIndex = currentPageNo - 1;

      const validationObj = {
        phoneNumberFields,
        formId: form_id,
        userCustomTexts,
        setErrorMessage,
        responseStore,
        hiddenFields,
        presentPage,
      };
      const { atLeastOneWrong, ctrls } = await validatePresentPage(validationObj);

      // Update field errors and scroll to first invalid field
      if (ctrls.length && delta > 0) {
        const errorCtrls = [];
        ctrls.forEach((ct) => {
          if (ct?._validationError) {
            if (Array.isArray(ct._validationError)) {
              ct._validationError.forEach((arr) => {
                if (arr?.some((erx) => erx !== '')) errorCtrls.push(ct);
              });
            } else {
              errorCtrls.push(ct);
            }
          }
        });

        if (errorCtrls.length) {
          document.getElementById(`fpl-${errorCtrls[0].internal_id}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }

        dispatch(updateFieldError({ ctrls, pageIndex }));
      }

      // Always allow switch to previous page
      if (!atLeastOneWrong || (atLeastOneWrong && delta < 0)) {
        enableChangePage();
        scrollToTop();
        setErrorMessage('');
      }
    } else {
      enableChangePage();
      scrollToTop();
    }
  };

  const isTimeElapsed = () => {
    setTimeUp({
      quizEnd: timer_purpose === 'quiz',
      eventEnd: timer_purpose === 'event',
    });
  };

  useEffect(() => {
    if (!presentPage) return;

    const { controls } = presentPage;

    if (formRules.length) {
      const triggerableControls = controls.filter((ctrl) => isTriggerField(triggeringFields, ctrl.internal_id));
      if (triggerableControls.length) {
        dispatch(doPageApplyLogic({ triggerableControls }));
      }
    }

    if (formCalculations.length) {
      const calculationOperands = controls.filter((ctrl) => isCalcOperandField(formCalculations, ctrl));
      if (calculationOperands.length) {
        dispatch(doPageApplyCalculations({ calculationOperands }));
      }
    }
  }, [formRules.length, formCalculations.length, JSON.stringify(presentPage)]);

  const ctrls = cloneDeep(formPages).flatMap((page) => {
    const isNotHiddenPages = !isHiddenField(page, hiddenFields);
    return isNotHiddenPages && page.controls;
  });

  // To be modified for used in the new payment flow
  // DON'T DELETE PLEASE: const paymentFieldTypes = ['paystack', 'paypal', 'stripe', 'flutterwave'];
  // DON'T DELETE PLEASE: ctrls.filter((ctrl) => paymentFieldTypes.includes(ctrl.type));
  const paymentCtrls = ctrls.filter((ctrl) => ctrl.props.is_inventory_payment);

  const isPaymentCompleted = paymentCtrls.every((control) => {
    const {
      props: { required },
      internal_id,
      value,
      type,
    } = control;
    const payment = payments[type][internal_id];
    return (!required && !payment?.isPaid) || (editingResponse && value) || payment?.isPaid;
  });

  const shouldRequestEsignAgreement = Boolean(
    enableESignature &&
    !eSign.auditTrail.signed &&
    ctrls.some(({ value, type, props: { required } }) => type === 'signature' && (required || value))
  );

  return (
    <FormPageBackgroundWrapper customCss={newCss} id={reshowIntroPage ? 'hide' : undefined}>
      <FormPageBackground
        pageBackground={formPageBackgroundStyles(formDesign)}
        formDirection={formDesign.design.form_direction}
        layoutType={getFormLayout(formDesign)}
        className="form-page-background"
        formSubmitted={formSubmitted}
        inCustomise={inCustomise}
        inForm={inForm}
        style={{
          '--form-dir': getFormLayout(formDesign),
        }}
      // customBackground={toHex(formDesign.old.page_background)}
      >
        <Container
          layoutType={getFormLayout(formDesign)}
          formPageStyles={formStyles(formDesign)}
          enableTimer={enableTimer}
          ref={formBodyRef}
          inForm={inForm}
          style={{
            '--form-width': `${formDesign.design.custom_form_width}%`,
            '--form-border-radius': `${formDesign.design.form_border_radius}px`,
          }}>
          <FormHeader formName={form.name} formDesign={formDesign} />

          <FormControlsWrapper style={formBodyStyles}>
            {inForm && hasIntroMessage && (
              <ReshowIntroPage
                onClick={() => dispatch(doReshowIntroPage())}
                style={{
                  '--form-body-label-color': formDesign.design.form_body_label_color,
                  '--form-text-color': toHex(formDesign.old.form_text_color),
                  '--font-size': formDesign.old.form_font_size,
                  '--font-family': formDesign.old.form_font,
                }}>
                {userCustomTexts.view_intro_page || defaultUserCustomTexts.view_intro_page}
              </ReshowIntroPage>
            )}

            <FormControlsFlexWrapper enableTimer={enableTimer} timerPosition={timer_position}>
              {!isEditedByUser && enableTimer && !formSubmitted && !submitError && (
                <Timer
                  pauseTime={submitInProgress || formSubmitted || !!submitError}
                  timerCountdownTo={timer_countdown_to}
                  timerDescription={timer_description}
                  isTimeElapsed={isTimeElapsed}
                  timerDisplay={timer_display}
                  timerPurpose={timer_purpose}
                  formDesign={formDesign}
                  timer={timer}
                />
              )}

              {openPaymentModal && (
                <PaymentModal
                  onPaymentCompleted={triggerFormSubmission}
                  isPaymentCompleted={isPaymentCompleted}
                  hiddenFields={hiddenFields}
                  formDesign={formDesign}
                  ctrls={paymentCtrls}
                />
              )}

              {eSign.openModal && <ESignPreviewModal onCompleted={triggerFormSubmission} form={form} />}

              <FormControls>
                {formPages.map(
                  (page) =>
                    !isHiddenField(page, hiddenFields) &&
                    page.internal_id === presentPage?.internal_id && (
                      <div key={`page-${page.internal_id}`} className="page">
                        {page.controls.map((ctrl) => {
                          if (shouldRender(ctrl, hiddenFields)) {
                            return (
                              <React.Fragment key={`ctrl-${ctrl.internal_id}`}>
                                <ErrorBoundary
                                  title={`Customise Form Field (${ctrl.internal_id}): ${ctrl.type
                                    .split('_')
                                    .join(' ')}`}>
                                  <PreviewFormField ctrl={ctrl} rowCtrls={rowCtrls} formDesign={formDesign} />
                                </ErrorBoundary>
                              </React.Fragment>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )
                )}
              </FormControls>
            </FormControlsFlexWrapper>
          </FormControlsWrapper>

          {presentPage && (
            <Pagination
              shouldRequestEsignAgreement={shouldRequestEsignAgreement}
              isSubmissionTriggered={isSubmissionTriggered}
              setSubmitInProgress={setSubmitInProgress}
              isPaymentCompleted={isPaymentCompleted}
              setFormSubmitted={setFormSubmitted}
              submitInProgress={submitInProgress}
              setErrorMessage={setErrorMessage}
              timerFinishDo={timer_finish_do}
              formBodyStyles={formBodyStyles}
              setSubmitError={setSubmitError}
              formSubmitted={formSubmitted}
              currentPageNo={currentPageNo}
              timerPurpose={timer_purpose}
              errorMessage={errorMessage}
              enableTimer={enableTimer}
              presentPage={presentPage}
              submitError={submitError}
              visiblePages={formPages}
              changePage={changePage}
              formDesign={formDesign}
              noOfPages={noOfPages}
              progress={progress}
              percent={percent}
              timeUp={timeUp}
              form={form}
            />
          )}

          <FormFooter formDesign={formDesign} />
          <FormWatermark useInvisibleRecaptcha={form.use_invisible_captcha} formDesign={formDesign} />
          {!inCustomise && <ThirdPartyShare socialOptions={socialOptions} formName={form.name} />}
          <ManuallyReplayOfflineSubmission formDesign={formDesign} />
        </Container>
      </FormPageBackground>
    </FormPageBackgroundWrapper>
  );
};

NewFormPreview.propTypes = {
  formDesign: PropTypes.shape(formDesignProps).isRequired,
  rowCtrls: PropTypes.objectOf(PropTypes.any).isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default NewFormPreview;
